import { forwardRef, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { toStreamUrl } from "@/lib/driveUrl";
import { detourLogo } from "@/lib/detour";

const MONTHS = ["JANVIER", "FÉVRIER", "MARS", "AVRIL", "MAI", "JUIN", "JUILLET", "AOÛT", "SEPTEMBRE", "OCTOBRE", "NOVEMBRE", "DÉCEMBRE"];

// Les images externes passent par un proxy du site : affichage fiable ET exportables en PDF/PNG.
// - Liens Google Drive → /api/drive-file/{id}
// - Autres liens externes (Google Photos résolu, URL directes) → /api/proxy-image
const imgUrl = (u) => {
  if (!u) return u;
  const streamed = toStreamUrl(u);
  if (streamed !== u) return streamed;
  if (/^https?:\/\//.test(u) && !u.startsWith(process.env.REACT_APP_BACKEND_URL)) {
    return `${process.env.REACT_APP_BACKEND_URL}/api/proxy-image?url=${encodeURIComponent(u)}`;
  }
  return u;
};

export function parseDate(dateStr) {
  const [y, m, d] = (dateStr || "").split("-").map(Number);
  if (!y || !m || !d) return null;
  return { year: y, day: d, month: MONTHS[m - 1] || "" };
}

/**
 * Texte multi-lignes : une ligne = un bloc avec line-height 1 et marge explicite.
 * Indispensable pour que l'export PDF/PNG place le texte exactement comme le navigateur
 * (html2canvas dessine le texte en bas de sa ligne quand line-height > font-size).
 */
function Lines({ text, fontSize, fontWeight = 700, gap = 4, letterSpacing }) {
  return (
    <div>
      {String(text).split("\n").map((line, i) => (
        <div key={i} style={{ fontSize, fontWeight, lineHeight: "1.08", marginTop: i > 0 ? gap : 0, letterSpacing, wordWrap: "break-word" }}>
          {line}
        </div>
      ))}
    </div>
  );
}

function useImageRatio(src) {
  const [ratio, setRatio] = useState(null);
  useEffect(() => setRatio(null), [src]);
  return [ratio, (e) => setRatio(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight)];
}

/**
 * Image recadrée pour remplir son cadre SANS déformation (object-fit n'est pas respecté à l'export).
 * overscan > 1 zoome très légèrement pour garantir le pleine page même si la photo a de petits bords.
 */
function CoverImg({ src, width, height, rounded = 0, border, rotate = 0, overscan = 1 }) {
  const [ratio, onLoad] = useImageRatio(src);
  const frameRatio = width / height;
  // maxWidth/maxHeight "none" : sans cela, le préfixage Tailwind (img { max-width: 100% }) plafonne
  // l'image à la largeur du cadre et la déforme quand elle doit dépasser pour couvrir.
  let imgStyle = { display: "block", opacity: 0, maxWidth: "none", maxHeight: "none" };
  if (ratio) {
    let w, h;
    if (ratio > frameRatio) { h = height; w = h * ratio; } else { w = width; h = w / ratio; }
    w = Math.ceil(w * overscan);
    h = Math.ceil(h * overscan);
    imgStyle = { ...imgStyle, position: "absolute", width: w, height: h, left: Math.round((width - w) / 2), top: Math.round((height - h) / 2), opacity: 1 };
  }
  return (
    <div style={{ position: "relative", width, height, overflow: "hidden", borderRadius: rounded, ...(border ? { border } : {}), ...(rotate ? { transform: `rotate(${rotate}deg)` } : {}) }}>
      <img src={src} alt="" crossOrigin="anonymous" style={imgStyle} onLoad={onLoad} onError={(e) => (e.currentTarget.style.display = "none")} />
    </div>
  );
}

/**
 * Photo secondaire : le cadre ÉPOUSE les proportions exactes de la photo (aucun recadrage).
 * Seule une photo démesurément haute est recadrée à maxHeight.
 */
function FitImg({ src, width, maxHeight = 280, rounded = 0, border, rotate = 0 }) {
  const [ratio, onLoad] = useImageRatio(src);
  let height = maxHeight;
  let imgStyle = { display: "block", opacity: 0, maxWidth: "none", maxHeight: "none" };
  if (ratio) {
    const naturalH = Math.round(width / ratio);
    if (naturalH <= maxHeight) {
      height = naturalH;
      imgStyle = { ...imgStyle, position: "absolute", width, height, left: 0, top: 0, opacity: 1 };
    } else {
      const h = Math.ceil(width / ratio);
      imgStyle = { ...imgStyle, position: "absolute", width, height: h, left: 0, top: Math.round((maxHeight - h) / 2), opacity: 1 };
    }
  }
  return (
    <div style={{ position: "relative", width, height, overflow: "hidden", borderRadius: rounded, ...(border ? { border } : {}), ...(rotate ? { transform: `rotate(${rotate}deg)` } : {}) }}>
      <img src={src} alt="" crossOrigin="anonymous" style={imgStyle} onLoad={onLoad} onError={(e) => (e.currentTarget.style.display = "none")} />
    </div>
  );
}

/** Logo de partenaire : sur pastille blanche arrondie par défaut (net dans tous les cas),
 *  ou détouré automatiquement si demandé (pour les logos déjà pensés pour la transparence).
 *  Sa taille est pilotée par le pied de page (auto-dimensionnement pour tenir sur une ligne). */
function SponsorLogo({ src, detour = false, height = 52, onRatio }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    if (detour) detourLogo(src).then((u) => alive && setUrl(u));
    else setUrl(src);
    return () => { alive = false; };
  }, [src, detour]);
  if (!url) return null;
  const innerH = detour ? height + 10 : height;
  if (detour) return <img src={url} alt="" style={{ height: innerH, width: "auto", display: "block" }} onLoad={(e) => onRatio?.(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight)} />;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", backgroundColor: "#FFFFFF", borderRadius: 10, padding: 5 }}>
      <img src={url} alt="" style={{ height, width: "auto", maxWidth: Math.max(80, height * 2.3), display: "block" }} onLoad={(e) => onRatio?.(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight)} />
    </span>
  );
}

/**
 * Affiche de concert au format A4 (700 × 990 px de base, ratio strict 210/297).
 * Mise en page en blocs simples + tableaux (pas de centrage flex) pour que l'export PDF/PNG
 * soit strictement identique à la prévisualisation. Hauteurs de ligne en pixels explicites.
 */
const PosterCanvas = forwardRef(function PosterCanvas({ poster }, ref) {
  const p = poster || {};
  const [qr, setQr] = useState(null);

  useEffect(() => {
    let alive = true;
    if (p.qr_url) {
      QRCode.toDataURL(p.qr_url, { margin: 0, width: 300, color: { dark: "#1E1B18", light: "#FFFFFF" } })
        .then((u) => alive && setQr(u))
        .catch(() => alive && setQr(null));
    } else {
      setQr(null);
    }
    return () => { alive = false; };
  }, [p.qr_url]);

  const theme = p.theme_color || "#3F6B3F";
  const ink = p.dark_text ? "#1E1B18" : "#FFFFFF";
  const dark = p.template === "sombre";
  const pct = (v) => Math.min(140, Math.max(60, Number(v) || 100)) / 100;
  const titleScale = pct(p.title_size);
  const titleSize = Math.round(44 * titleScale);
  const title2Size = Math.round(20 * pct(p.title2_size));
  const subtitleSize = Math.round(37 * pct(p.subtitle_size));
  const dateScale = pct(p.date_size);
  const infoSize = Math.round(19 * pct(p.info_size));
  const panelScale = pct(p.panel_size);
  const contactScale = pct(p.contact_size);
  const date = parseDate(p.date);
  const photos = (p.secondary_images || []).filter(Boolean).slice(0, 2);
  const logos = (p.logos || []).filter(Boolean);
  const hasFooter = logos.length > 0 || !!p.qr_url || !!p.contact;
  const bottomClearance = hasFooter ? 146 : 24;

  // Mesures des logos pour l'auto-dimensionnement du pied de page (une seule ligne)
  const [logoRatios, setLogoRatios] = useState([]);
  useEffect(() => {
    setLogoRatios((logos || []).map(() => 1.6));
  }, [logos.length]);
  // Le QR se réduit si la ligne est chargée (beaucoup de logos + contact)
  const qrTileSize = qr ? (logos.length >= 3 ? 76 : logos.length >= 2 && p.contact ? 86 : 94) : 0;

  // Cadre de couleur : le fond arrondi (éventuellement penché) est un calque SÉPARÉ du texte.
  // html2canvas décale verticalement le texte à l'intérieur des éléments tournés (transform: rotate) ;
  // en séparant fond et texte, l'export est strictement identique à la prévisualisation.
  const Frame = ({ rotate = 0, radius = 22, opacity, style = {}, children }) => (
    <div style={{ position: "absolute", color: ink, textAlign: "center", ...style }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: theme,
          borderRadius: radius,
          ...(rotate ? { transform: `rotate(${rotate}deg)` } : {}),
          ...(opacity ? { opacity } : {}),
        }}
      />
      <div style={{ position: "relative" }}>{children}</div>
    </div>
  );

  return (
    <div
      ref={ref}
      data-testid="poster-canvas"
      style={{
        position: "relative",
        width: 700,
        height: 990,
        overflow: "hidden",
        backgroundColor: dark ? "#181C17" : theme,
        fontFamily: "Arial, Helvetica, sans-serif",
      }}
    >
      {/* Photo de fond : léger zoom de sécurité pour occuper toute la page, sans déformer */}
      {p.main_image && (
        <div style={{ position: "absolute", top: 0, left: 0, width: 700, height: 990 }}>
          <CoverImg src={imgUrl(p.main_image)} width={700} height={990} overscan={1.07} />
          {dark && <div style={{ position: "absolute", top: 0, left: 0, width: 700, height: 990, backgroundColor: "rgba(8, 10, 8, 0.45)" }} />}
        </div>
      )}

      {/* Bloc date, en haut à gauche — forme de galet, légèrement penchée */}
      {date && (
        <Frame rotate={-1.5} radius="26px 34px 24px 38px" style={{ top: 24, left: 24, minWidth: 168, padding: "16px 20px" }}>
          <div style={{ fontSize: Math.round(22 * dateScale), fontWeight: 800, letterSpacing: 3, lineHeight: "1", marginBottom: 4 }}>{date.year}</div>
          <div style={{ fontSize: Math.round(68 * dateScale), fontWeight: 900, lineHeight: "1", marginBottom: 6 }}>{date.day}</div>
          <div style={{ fontSize: Math.round(19 * dateScale), fontWeight: 800, letterSpacing: 1, lineHeight: "1.05", whiteSpace: "nowrap" }}>{date.month}</div>
          {p.time && <div style={{ fontSize: Math.round(22 * dateScale), fontWeight: 800, lineHeight: "1", marginTop: 10 }}>{p.time}</div>}
        </Frame>
      )}

      {/* Titre principal */}
      {p.title && (
        <Frame rotate={1} radius="22px 30px 24px 26px" style={{ top: 24, right: 24, maxWidth: 470, padding: "18px 28px" }}>
          <Lines text={p.title.toUpperCase()} fontSize={titleSize} fontWeight={900} gap={3} />
          {p.title_2 && (
            <div style={{ marginTop: 8 }}>
              <Lines text={p.title_2} fontSize={title2Size} fontWeight={800} gap={3} />
            </div>
          )}
        </Frame>
      )}

      {/* Sous-titre / nom de l'événement */}
      {p.subtitle && (
        <Frame rotate={-1.8} radius="20px 26px 28px 22px" style={{ top: 440, left: 24, maxWidth: 460, padding: "12px 24px" }}>
          <Lines text={p.subtitle.toUpperCase()} fontSize={subtitleSize} fontWeight={900} gap={3} />
        </Frame>
      )}

      {/* Photos secondaires (cadre aux proportions exactes de chaque photo) + infos, en bas à gauche */}
      {(photos.length > 0 || p.info) && (
        <div style={{ position: "absolute", left: 24, bottom: bottomClearance, maxWidth: 340 }}>
          {photos.length > 0 && (
            <div style={{ fontSize: 0 }}>
              {photos.map((u, i) => (
                <div key={i} style={{ display: "inline-block", verticalAlign: "bottom", marginLeft: i > 0 ? 14 : 0 }}>
                  <FitImg
                    src={imgUrl(u)}
                    width={photos.length > 1 ? 158 : 320}
                    maxHeight={220}
                    rounded={18}
                    border="4px solid rgba(255,255,255,0.9)"
                    rotate={photos.length > 1 ? (i === 0 ? -2 : 1.6) : -1}
                  />
                </div>
              ))}
            </div>
          )}
          {p.info && (
            <Frame rotate={1.2} radius="22px 26px 20px 28px" style={{ position: "relative", padding: "12px 20px", marginTop: photos.length > 0 ? 14 : 0 }}>
              <Lines text={p.info} fontSize={infoSize} fontWeight={800} gap={4} />
            </Frame>
          )}
        </div>
      )}

      {/* Tarifs et lieu, en bas à droite — panneau semi-opaque pour rester lisible sur la photo */}
      {(p.prices || p.venue_name || p.venue_address) && (
        <Frame rotate={-0.6} radius="26px 22px 30px 24px" opacity={0.94} style={{ right: 24, bottom: bottomClearance, width: 246, padding: "16px 14px" }}>
          {p.prices && <Lines text={p.prices} fontSize={Math.round(18 * panelScale)} fontWeight={900} gap={5} />}
          {(p.venue_name || p.venue_address) && (
            <div style={{ marginTop: p.prices ? 14 : 0 }}>
              {p.venue_name && <div style={{ fontSize: Math.round(16 * panelScale), fontWeight: 800, lineHeight: "1.1" }}>{p.venue_name}</div>}
              {p.venue_address && (
                <div style={{ marginTop: 4 }}>
                  <Lines text={p.venue_address} fontSize={Math.round(14 * panelScale)} fontWeight={700} gap={3} />
                </div>
              )}
            </div>
          )}
        </Frame>
      )}

      {/* Pied de page organique : vague de la couleur du thème — logos, contact et QR TIENNENT TOUJOURS sur une seule ligne
          (auto-dimensionnement : on réduit logos et QR jusqu'à ce que tout rentre) */}
      {hasFooter && (
        <div
          style={{
            position: "absolute",
            left: -20,
            right: -20,
            bottom: -10,
            minHeight: 128,
            backgroundColor: theme,
            borderRadius: "50% 50% 0 0 / 44px 36px 0 0",
            padding: "32px 44px 12px",
          }}
        >
          {(() => {
            // Mesures : logos mesurés à leur chargement, contact estimé, largeur disponible ≈ 652 px
            const qrTile = qr ? qrTileSize : 0;
            const contactW = p.contact
              ? Math.min(300, Math.max(170, String(p.contact).split("\n").reduce((m, l) => Math.max(m, l.length), 0) * 7.4 * contactScale + 20))
              : 0;
            const gaps = (logos.length ? logos.length * 16 : 0) + (p.contact ? 20 : 0) + (qr ? 20 : 0);
            const avail = 652 - qrTile - contactW - gaps;
            const naturalW = logoRatios.reduce((s, r, i) => s + Math.min(r, 2.3) * 52 + 10 + (i > 0 ? 16 : 0), 0);
            const scale = logos.length && naturalW > avail ? Math.max(0.55, Math.min(1, (avail + (logos.length > 1 ? (logos.length - 1) * 16 : 0)) / naturalW)) : 1;
            const logoH = Math.round(52 * scale);
            return (
              <div style={{ display: "table", width: "100%" }}>
                <div style={{ display: "table-cell", verticalAlign: "middle" }}>
                  {logos.map((u, i) => (
                    <span key={i} style={{ display: "inline-block", verticalAlign: "middle", marginRight: 16 }}>
                      <SponsorLogo
                        src={imgUrl(u)}
                        detour={p.detour_logos}
                        height={logoH}
                        onRatio={(r) => setLogoRatios((prev) => (prev[i] === r ? prev : prev.map((v, j) => (j === i ? r : v))))}
                      />
                    </span>
                  ))}
                </div>
                {p.contact && (
                  <div style={{ display: "table-cell", verticalAlign: "middle", textAlign: "center", color: ink, padding: "0 10px" }}>
                    <div style={{ fontSize: Math.round(14 * contactScale), fontWeight: 800, lineHeight: "1.1" }}>Informations et réservations</div>
                    <div style={{ marginTop: 3 }}>
                      <Lines text={p.contact} fontSize={12.5 * contactScale} fontWeight={700} gap={2} />
                    </div>
                  </div>
                )}
                {qr && (
                  <div style={{ display: "table-cell", verticalAlign: "middle", textAlign: "right", width: qrTile }}>
                    <div style={{ display: "inline-block", backgroundColor: "#FFFFFF", borderRadius: 14, padding: 7, transform: "rotate(2deg)" }}>
                      <img src={qr} alt="QR code" style={{ width: qrTile - 14, height: qrTile - 14, display: "block" }} data-testid="poster-qr" />
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
});

export default PosterCanvas;

/** Affiche réduite proprement à la largeur de son conteneur (prévisualisation admin, page Actualités).
 *  La largeur est mesurée en direct : la fiche n'est jamais coupée, même sur mobile. */
export function ScaledPoster({ poster }) {
  const wrapRef = useRef(null);
  const [w, setW] = useState(350);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setW(Math.max(120, el.clientWidth));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  const scale = w / 700;
  return (
    <div ref={wrapRef} style={{ width: "100%", height: Math.round(990 * scale), overflow: "hidden", position: "relative" }}>
      <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
        <PosterCanvas poster={poster} />
      </div>
    </div>
  );
}
